import { useState, useEffect } from "react";
import { Link } from "react-router-dom";

const CookieBanner = () => {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [prefs, setPrefs] = useState({
    essential: true,
    analytics: true,
    marketing: false,
  });

  useEffect(() => {
    const consent = localStorage.getItem("cookie-consent");
    if (!consent) {
      const timer = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(timer);
    }
  }, []);

  const acceptAll = () => {
    localStorage.setItem("cookie-consent", JSON.stringify({ essential: true, analytics: true, marketing: true }));
    setVisible(false);
  };

  const rejectAll = () => {
    localStorage.setItem("cookie-consent", JSON.stringify({ essential: true, analytics: false, marketing: false }));
    setVisible(false);
  };

  const savePrefs = () => {
    localStorage.setItem("cookie-consent", JSON.stringify(prefs));
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[9999] p-4 md:p-6">
      <div
        className="max-w-4xl mx-auto bg-white rounded-2xl overflow-hidden"
        style={{ boxShadow: "0 8px 40px rgba(0,0,0,0.15)" }}
      >
        {/* Main Banner */}
        <div className="p-5 md:p-6">
          <div className="flex items-start gap-4">
            {/* Icon */}
            <div className="w-10 h-10 flex items-center justify-center bg-brand-green/10 rounded-xl flex-shrink-0 mt-0.5">
              <i className="ri-shield-check-line text-brand-green text-xl"></i>
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <h3 className="font-heading font-bold text-brand-dark text-base mb-1">
                We Use Cookies
              </h3>
              <p className="font-body text-gray-500 text-sm leading-relaxed">
                We use cookies to enhance your browsing experience, analyze site traffic, and personalize content. By clicking <strong className="text-brand-dark">"Accept All"</strong>, you consent to our use of cookies.{" "}
                <Link
                  to="/cookie-policy"
                  className="text-brand-green hover:underline cursor-pointer font-medium"
                >
                  Learn more
                </Link>
              </p>

              {/* Expandable preferences */}
              {showDetails && (
                <div className="mt-4 flex flex-col gap-2 border-t border-gray-100 pt-4">
                  {[
                    {
                      key: "essential" as const,
                      label: "Essential Cookies",
                      desc: "Required for the website to function. Cannot be disabled.",
                      locked: true,
                    },
                    {
                      key: "analytics" as const,
                      label: "Analytics Cookies",
                      desc: "Help us understand how visitors interact with our website.",
                      locked: false,
                    },
                    {
                      key: "marketing" as const,
                      label: "Marketing Cookies",
                      desc: "Used to show relevant ads and track campaign performance.",
                      locked: false,
                    },
                  ].map(({ key, label, desc, locked }) => (
                    <div key={key} className="flex items-start justify-between gap-3 py-2">
                      <div>
                        <p className="font-heading font-semibold text-brand-dark text-xs">{label}</p>
                        <p className="font-body text-gray-400 text-xs mt-0.5">{desc}</p>
                      </div>
                      <button
                        onClick={() => !locked && setPrefs((p) => ({ ...p, [key]: !p[key] }))}
                        disabled={locked}
                        className={`relative w-10 h-5 rounded-full transition-all flex-shrink-0 mt-0.5 ${
                          locked ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
                        } ${prefs[key] ? "bg-brand-green" : "bg-gray-300"}`}
                        aria-label={`Toggle ${label}`}
                      >
                        <div
                          className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-all ${
                            prefs[key] ? "left-5" : "left-0.5"
                          }`}
                        />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-gray-100">
            <button
              onClick={acceptAll}
              className="bg-brand-green hover:bg-brand-green-dark text-white px-5 py-2.5 rounded-full font-heading font-semibold text-xs transition-all cursor-pointer whitespace-nowrap"
            >
              Accept All
            </button>
            <button
              onClick={rejectAll}
              className="bg-gray-100 hover:bg-gray-200 text-brand-dark px-5 py-2.5 rounded-full font-heading font-semibold text-xs transition-all cursor-pointer whitespace-nowrap"
            >
              Reject All
            </button>
            {showDetails ? (
              <button
                onClick={savePrefs}
                className="border border-brand-green text-brand-green hover:bg-brand-green/5 px-5 py-2.5 rounded-full font-heading font-semibold text-xs transition-all cursor-pointer whitespace-nowrap"
              >
                Save Preferences
              </button>
            ) : (
              <button
                onClick={() => setShowDetails(true)}
                className="text-gray-400 hover:text-brand-dark px-3 py-2.5 font-heading font-semibold text-xs transition-all cursor-pointer whitespace-nowrap underline"
              >
                Customize
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
